/**
 * 앱 아이콘 단일 창구 — Phosphor Icons(MIT, phosphoricons.com)의 Regular 굵기를 쓰임새가 드러나는
 * 이름으로 내보낸다. 화면 코드는 `~icons/...` 를 직접 import 하지 않고 여기서만 가져온다.
 *
 * 한 겹 선 아이콘이라 어떤 글자색 위에서도 무게가 같고, 크기는 메뉴 16px · 버튼 안 14~16px · 빈 자리 36px 로 쓴다.
 *
 * 굵기는 Regular 하나만 쓴다. 강조가 필요하면 굵기를 바꾸지 말고 글자색(text-clay-text)으로 준다 —
 * Bold·Fill 이 섞이면 한 화면 안에서 아이콘 무게가 들쭉날쭉해진다.
 * 예외는 문서 폴더 아이콘(…Fill) 하나다 — 탭 이름 앞에서 사용자가 고르는 표지라 채운 굵기로 또렷이
 * 보이게 했다. 이름 끝에 Fill 을 붙여 내보내고, 문서 폴더(src/components/documents/folderIcons.ts) 밖에서는 쓰지 않는다.
 *
 * 빌드: unplugin-icons 가 ~icons/ph/<이름> 을 SVG 컴포넌트로 컴파일한다(런타임 요청 없음,
 * 쓰는 것만 번들). 색은 currentColor — 글자색(text-clay-text/text-clay-text-sub)을 따라간다.
 *
 * 새 아이콘이 필요하면 여기에 두 줄(import + export) 추가한다. 이름은
 * https://icon-sets.iconify.design/ph/ 에서 접미사 없는 것(Regular)을 고른다.
 */
import { useEffect, useState, type ComponentType, type CSSProperties, type SVGProps } from "react";
import RawPenLine from "~icons/ph/pencil-simple-line"; // Daily
import RawHistory from "~icons/ph/clock-counter-clockwise"; // 내 기록
import RawCalendar from "~icons/ph/calendar-blank"; // 달력
import RawBell from "~icons/ph/bell"; // 알림
import RawLightbulb from "~icons/ph/lightbulb-filament"; // 리포트 — AI 가 쓰는 화면(반짝이는 Ask AI 몫)
import RawBlueprint from "~icons/ph/blueprint"; // 프로젝트 — 설계 사무소의 도면
import RawBooks from "~icons/ph/books"; // 문서 폴더 — 자료실
import RawKanban from "~icons/ph/kanban"; // Daily 판으로 보기 — 높이가 다른 판을 쌓는 waterfall
import RawColumns from "~icons/ph/columns"; // Daily 보드 — 프로젝트 열을 가로로
import RawSettings from "~icons/ph/gear-six"; // 설정(개인)
import RawAdminSettings from "~icons/ph/sliders-horizontal"; // 관리자 설정 — 사무소 전체를 조정
import RawSidebar from "~icons/ph/sidebar-simple"; // 사이드바 펼쳐 두기·접어 두기
import RawBriefcase from "~icons/ph/briefcase"; // 프로젝트 관리
import RawLockKey from "~icons/ph/lock-key"; // 권한 관리 — 누가 무엇을 열 수 있는가
import RawLock from "~icons/ph/lock-simple"; // Daily 잠금 — 제출 시간이 지난 날
import RawGlobe from "~icons/ph/globe"; // 공개 범위 — 전 직원
import RawMinus from "~icons/ph/minus"; // 스테퍼 — 줄이기
import RawSealCheck from "~icons/ph/seal-check"; // AI 요약 검토 — 사람이 확인·검수하는 곳
import RawClock from "~icons/ph/clock"; // 근태 관리
import RawIdBadge from "~icons/ph/identification-badge"; // 사원증
import RawLogOut from "~icons/ph/sign-out";
import RawShieldUser from "~icons/ph/shield-check"; // 권한 없음 자리표시
import RawInbox from "~icons/ph/tray"; // 빈 상태 자리표시
import RawAlertTriangle from "~icons/ph/warning"; // 불러오기 실패 자리표시·위험 배너
import RawWarningFill from "~icons/ph/warning-fill"; // 공정표의 마감 넘긴 단계
import RawWarningCircle from "~icons/ph/warning-circle"; // 휴가 신청을 낼 수 없는 까닭
import RawInfoCircle from "~icons/ph/info"; // 안내 배너
import RawX from "~icons/ph/x";
import RawChevronDown from "~icons/ph/caret-down";
import RawChevronRight from "~icons/ph/caret-right";
import RawOpenPage from "~icons/ph/arrow-square-out"; // 그 자리를 떠나 제 화면으로 연다, Weekly 보고서 열기
import RawChevronLeft from "~icons/ph/caret-left";
import RawPlus from "~icons/ph/plus";
import RawArrowRight from "~icons/ph/arrow-right"; // Daily 내일 계획으로
import RawSend from "~icons/ph/paper-plane-tilt"; // Daily 제출
import RawRefreshCw from "~icons/ph/arrows-clockwise";
import RawGoogle from "~icons/logos/google-icon"; // 로그인 — Google SSO. Google 브랜드 색 그대로(logos 세트, CC0)
import RawAiFace from "~icons/solar/face-scan-square-bold-duotone"; // Ask AI 표지(예외 — 아래 설명)
import RawArrowUp from "~icons/ph/arrow-up"; // 어시스턴트 보내기
import RawMenu from "~icons/ph/list"; // 어시스턴트 대화 목록
import RawCheck from "~icons/ph/check"; // 메뉴의 선택 표시
import RawChevronUp from "~icons/ph/caret-up";
import RawCheckCircle from "~icons/ph/check-circle"; // 토스트 — 성공
import RawCheckCircleFill from "~icons/ph/check-circle-fill"; // 직원 표 — 2차 인증 켜짐 · 공정표의 끝난 단계
import RawXCircle from "~icons/ph/x-circle"; // 토스트 — 실패
import RawTable from "~icons/ph/table"; // 표 보기 — 문서 폴더, Daily
import RawTextT from "~icons/ph/text-t"; // 문서 폴더 열 — 텍스트
import RawHash from "~icons/ph/hash"; // 문서 폴더 열 — 숫자
import RawCaretCircleDown from "~icons/ph/caret-circle-down"; // 문서 폴더 열 — 선택
import RawSortAscending from "~icons/ph/sort-ascending"; // 문서 폴더 정렬 — 오름차순
import RawSortDescending from "~icons/ph/sort-descending"; // 문서 폴더 정렬 — 내림차순
import RawTag from "~icons/ph/tag"; // 문서 폴더 열 — 다중 선택
import RawUser from "~icons/ph/user"; // 문서 폴더 열 — 사람
import RawCheckSquare from "~icons/ph/check-square"; // 문서 폴더 열 — 체크박스
import RawLinkSimple from "~icons/ph/link-simple"; // 문서 폴더 열 — URL
import RawPaperclip from "~icons/ph/paperclip"; // 문서 폴더 열 — 파일(NAS 경로)
import RawDotsThree from "~icons/ph/dots-three"; // 더보기 메뉴
import RawSearch from "~icons/ph/magnifying-glass"; // 검색
import RawArrowLeft from "~icons/ph/arrow-left"; // 뒤로
import RawTrash from "~icons/ph/trash"; // 지우기
import RawUsers from "~icons/ph/users"; // 직원 관리
import RawImageSquare from "~icons/ph/image-square"; // 이미지 올리기 — 끌어 놓는 자리
// 문서 폴더 아이콘 — Fill 굵기(위 머리말의 예외). src/components/documents/folderIcons.ts 에서만 쓴다.
import RawFolderFill from "~icons/ph/folder-fill";
import RawFileTextFill from "~icons/ph/file-text-fill";
import RawBooksFill from "~icons/ph/books-fill";
import RawNotebookFill from "~icons/ph/notebook-fill";
import RawClipboardFill from "~icons/ph/clipboard-text-fill";
import RawArchiveFill from "~icons/ph/archive-fill";
import RawBookmarkFill from "~icons/ph/bookmark-simple-fill";
import RawTagFill from "~icons/ph/tag-fill";
import RawBuildingsFill from "~icons/ph/buildings-fill";
import RawHouseFill from "~icons/ph/house-fill";
import RawHouse from "~icons/ph/house"; // 대시보드
import RawBlueprintFill from "~icons/ph/blueprint-fill";
import RawRulerFill from "~icons/ph/ruler-fill";
import RawHammerFill from "~icons/ph/hammer-fill";
import RawWrenchFill from "~icons/ph/wrench-fill";
import RawPaintBrushFill from "~icons/ph/paint-brush-fill";
import RawPaletteFill from "~icons/ph/palette-fill";
import RawTruckFill from "~icons/ph/truck-fill";
import RawCarFill from "~icons/ph/car-fill";
import RawAirplaneFill from "~icons/ph/airplane-tilt-fill";
import RawIslandFill from "~icons/ph/island-fill"; // 연차 부여 — 휴가
import RawUsersFill from "~icons/ph/users-fill";
import RawUserFill from "~icons/ph/user-fill";
import RawHandshakeFill from "~icons/ph/handshake-fill";
import RawPhoneFill from "~icons/ph/phone-fill";
import RawEnvelopeFill from "~icons/ph/envelope-simple-fill";
import RawChatFill from "~icons/ph/chat-circle-fill";
import RawMegaphoneFill from "~icons/ph/megaphone-fill";
import RawCalendarFill from "~icons/ph/calendar-blank-fill";
import RawClockFill from "~icons/ph/clock-fill";
import RawBriefcaseFill from "~icons/ph/briefcase-fill";
import RawReceiptFill from "~icons/ph/receipt-fill";
import RawCoinsFill from "~icons/ph/coins-fill";
import RawWalletFill from "~icons/ph/wallet-fill";
import RawShoppingCartFill from "~icons/ph/shopping-cart-fill";
import RawGiftFill from "~icons/ph/gift-fill";
import RawCoffeeFill from "~icons/ph/coffee-fill";
import RawForkKnifeFill from "~icons/ph/fork-knife-fill";
import RawMapPinFill from "~icons/ph/map-pin-fill";
import RawGlobeFill from "~icons/ph/globe-hemisphere-east-fill";
import RawFlagFill from "~icons/ph/flag-fill";
import RawStarFill from "~icons/ph/star-fill";
import RawHeartFill from "~icons/ph/heart-fill";
import RawLightbulbFill from "~icons/ph/lightbulb-filament-fill";
import RawImageFill from "~icons/ph/image-square-fill";
import RawCameraFill from "~icons/ph/camera-fill";
import RawFilmFill from "~icons/ph/film-strip-fill";
import RawMusicFill from "~icons/ph/music-notes-fill";
import RawLeafFill from "~icons/ph/leaf-fill";
import RawSunFill from "~icons/ph/sun-fill";
import RawGraduationFill from "~icons/ph/graduation-cap-fill";
import RawFirstAidFill from "~icons/ph/first-aid-fill";
import RawTrophyFill from "~icons/ph/trophy-fill";
import RawRocketFill from "~icons/ph/rocket-launch-fill";
import RawPuzzleFill from "~icons/ph/puzzle-piece-fill";
import RawKeyFill from "~icons/ph/key-fill";
import RawGearFill from "~icons/ph/gear-fill";
import RawLinkFill from "~icons/ph/link-simple-fill";
import RawGrip from "~icons/ph/dots-six"; // 끌어 옮기기 손잡이(가로 점 — 열 머리 위쪽 가장자리)
import RawCaretUpDown from "~icons/ph/caret-up-down"; // 고르는 자리 — 프로젝트 상세 제목의 프로젝트 전환

/** 호출 규약 — size(px), className. strokeWidth 는 받되 무시한다(굵기는 Regular 고정). */
export interface IconProps {
  size?: number | string;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
  "aria-hidden"?: boolean | "true" | "false";
  "aria-label"?: string;
}
export type IconComponent = ComponentType<IconProps>;

function mk(Raw: ComponentType<SVGProps<SVGSVGElement>>): IconComponent {
  const Icon: IconComponent = ({ size = 24, strokeWidth: _sw, className, ...rest }) => (
    <Raw
      width={size}
      height={size}
      className={className}
      aria-hidden={rest["aria-label"] ? undefined : true}
      {...rest}
    />
  );
  Icon.displayName = Raw.displayName ?? "Icon";
  return Icon;
}

export const PenLine = mk(RawPenLine);
export const History = mk(RawHistory);
export const Calendar = mk(RawCalendar);
export const Lightbulb = mk(RawLightbulb);
export const Blueprint = mk(RawBlueprint);
export const Books = mk(RawBooks);
export const Kanban = mk(RawKanban);
export const Columns = mk(RawColumns);
export const Settings = mk(RawSettings);
export const AdminSettings = mk(RawAdminSettings);
export const SidebarToggle = mk(RawSidebar);
export const Briefcase = mk(RawBriefcase);
export const LockKey = mk(RawLockKey);
export const Lock = mk(RawLock);
export const Globe = mk(RawGlobe);
export const Minus = mk(RawMinus);
export const SealCheck = mk(RawSealCheck);
export const Clock = mk(RawClock);
export const Bell = mk(RawBell);
export const IdBadge = mk(RawIdBadge);
export const LogOut = mk(RawLogOut);
export const ShieldUser = mk(RawShieldUser);
export const Inbox = mk(RawInbox);
export const AlertTriangle = mk(RawAlertTriangle);
export const WarningFill = mk(RawWarningFill);
export const WarningCircle = mk(RawWarningCircle);
export const InfoCircle = mk(RawInfoCircle);
export const X = mk(RawX);
export const ChevronDown = mk(RawChevronDown);
export const ChevronRight = mk(RawChevronRight);
export const OpenPage = mk(RawOpenPage);
export const ChevronLeft = mk(RawChevronLeft);
export const Plus = mk(RawPlus);
export const ArrowRight = mk(RawArrowRight);
export const Send = mk(RawSend);
export const RefreshCw = mk(RawRefreshCw);
export const Google = mk(RawGoogle);
export const AiFace = mk(RawAiFace);
export const ArrowUp = mk(RawArrowUp);
export const Menu = mk(RawMenu);
export const Check = mk(RawCheck);
export const ChevronUp = mk(RawChevronUp);
export const CheckCircle = mk(RawCheckCircle);
export const CheckCircleFill = mk(RawCheckCircleFill);
export const XCircle = mk(RawXCircle);
export const Table = mk(RawTable);
export const TextT = mk(RawTextT);
export const Hash = mk(RawHash);
export const CaretCircleDown = mk(RawCaretCircleDown);
export const SortAscending = mk(RawSortAscending);
export const SortDescending = mk(RawSortDescending);
export const Tag = mk(RawTag);
export const User = mk(RawUser);
export const CheckSquare = mk(RawCheckSquare);
export const LinkSimple = mk(RawLinkSimple);
export const Paperclip = mk(RawPaperclip);
export const DotsThree = mk(RawDotsThree);
export const Search = mk(RawSearch);
export const ArrowLeft = mk(RawArrowLeft);
export const Trash = mk(RawTrash);
export const Users = mk(RawUsers);
export const Grip = mk(RawGrip);
export const ImageSquare = mk(RawImageSquare);
export const FolderFill = mk(RawFolderFill);
export const FileTextFill = mk(RawFileTextFill);
export const BooksFill = mk(RawBooksFill);
export const NotebookFill = mk(RawNotebookFill);
export const ClipboardFill = mk(RawClipboardFill);
export const ArchiveFill = mk(RawArchiveFill);
export const BookmarkFill = mk(RawBookmarkFill);
export const TagFill = mk(RawTagFill);
export const BuildingsFill = mk(RawBuildingsFill);
export const HouseFill = mk(RawHouseFill);
export const House = mk(RawHouse);
export const BlueprintFill = mk(RawBlueprintFill);
export const RulerFill = mk(RawRulerFill);
export const HammerFill = mk(RawHammerFill);
export const WrenchFill = mk(RawWrenchFill);
export const PaintBrushFill = mk(RawPaintBrushFill);
export const PaletteFill = mk(RawPaletteFill);
export const TruckFill = mk(RawTruckFill);
export const CarFill = mk(RawCarFill);
export const AirplaneFill = mk(RawAirplaneFill);
export const IslandFill = mk(RawIslandFill);
export const UsersFill = mk(RawUsersFill);
export const UserFill = mk(RawUserFill);
export const HandshakeFill = mk(RawHandshakeFill);
export const PhoneFill = mk(RawPhoneFill);
export const EnvelopeFill = mk(RawEnvelopeFill);
export const ChatFill = mk(RawChatFill);
export const MegaphoneFill = mk(RawMegaphoneFill);
export const CalendarFill = mk(RawCalendarFill);
export const ClockFill = mk(RawClockFill);
export const BriefcaseFill = mk(RawBriefcaseFill);
export const ReceiptFill = mk(RawReceiptFill);
export const CoinsFill = mk(RawCoinsFill);
export const WalletFill = mk(RawWalletFill);
export const ShoppingCartFill = mk(RawShoppingCartFill);
export const GiftFill = mk(RawGiftFill);
export const CoffeeFill = mk(RawCoffeeFill);
export const ForkKnifeFill = mk(RawForkKnifeFill);
export const MapPinFill = mk(RawMapPinFill);
export const GlobeFill = mk(RawGlobeFill);
export const FlagFill = mk(RawFlagFill);
export const StarFill = mk(RawStarFill);
export const HeartFill = mk(RawHeartFill);
export const LightbulbFill = mk(RawLightbulbFill);
export const ImageFill = mk(RawImageFill);
export const CameraFill = mk(RawCameraFill);
export const FilmFill = mk(RawFilmFill);
export const MusicFill = mk(RawMusicFill);
export const LeafFill = mk(RawLeafFill);
export const SunFill = mk(RawSunFill);
export const GraduationFill = mk(RawGraduationFill);
export const FirstAidFill = mk(RawFirstAidFill);
export const TrophyFill = mk(RawTrophyFill);
export const RocketFill = mk(RawRocketFill);
export const PuzzleFill = mk(RawPuzzleFill);
export const KeyFill = mk(RawKeyFill);
export const GearFill = mk(RawGearFill);
export const LinkFill = mk(RawLinkFill);
export const CaretUpDown = mk(RawCaretUpDown);

/** 다음 자정까지 남은 ms — 날짜가 바뀌는 순간 다시 그리려고. */
const msUntilMidnight = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();
};

/**
 * 오늘 날짜가 찍힌 달력 — 근태/휴가 메뉴 몫. ph/calendar-blank 의 외곽선에 날짜 숫자를 얹었다.
 * 사이드바는 한 번 뜨면 오래 머무르므로 자정마다 숫자를 갈아 끼운다.
 */
export const CalendarToday: IconComponent = ({
  size = 24,
  strokeWidth: _sw,
  className,
  ...rest
}) => {
  const [day, setDay] = useState(() => new Date().getDate());
  useEffect(() => {
    const timer = setTimeout(() => setDay(new Date().getDate()), msUntilMidnight() + 1000);
    return () => clearTimeout(timer);
  }, [day]);
  return (
    <svg
      viewBox="0 0 256 256"
      width={size}
      height={size}
      className={className}
      aria-hidden={rest["aria-label"] ? undefined : true}
      {...rest}
    >
      <path
        fill="currentColor"
        d="M208 32h-24v-8a8 8 0 0 0-16 0v8H88v-8a8 8 0 0 0-16 0v8H48a16 16 0 0 0-16 16v160a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16M72 48v8a8 8 0 0 0 16 0v-8h80v8a8 8 0 0 0 16 0v-8h24v32H48V48Zm136 160H48V96h160z"
      />
      <text
        x="128"
        y="154"
        fill="currentColor"
        fontSize="100"
        fontWeight="600"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {day}
      </text>
    </svg>
  );
};
